import { createContext, useContext, useState } from 'react';
import { api } from '../../services/api';
import { toast } from 'react-toastify';
import CookiesService from '../../services/cookies';

const LancamentoPerdasContext = createContext();
export const useLancamentoPerdas = () => useContext(LancamentoPerdasContext);

export const LancamentoPerdasProvider = ({ children, modoBatch = false }) => {
    const userLogin = CookiesService.getUserLogin();

    const [showModal, setShowModal] = useState(false);
    const [showConfirmacao, setShowConfirmacao] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const [pecas, setPecas] = useState([]);
    const [amostra, setAmostra] = useState({});
    const [motivos, setMotivos] = useState([]);
    const [motivoSelecionado, setMotivoSelecionado] = useState(-1);
    const [destino, setDestino] = useState('');
    const [observacao, setObservacao] = useState('');
    const [responsavel, setResponsavel] = useState(userLogin);
    const [requisitante, setRequisitante] = useState('CONTROLE DE QUALIDADE');
    const [reqMaterial, setReqMaterial] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [errosLancamento, setErrosLancamento] = useState([]);

    const [callbackFinalizar, setCallbackFinalizar] = useState(null);

    const [habilitarModoLote] = useState(modoBatch);
    const [isBatchMode, setIsBatchMode] = useState(false);
    const [quantidadeBatch, setQuantidadeBatch] = useState('');
    const [motivoBatch, setMotivoBatch] = useState(-1);


    // Carregar motivos
    const carregarMotivos = async () => {
        try {
            setIsLoading(true);
            const response = await api.get('/mp/lancamento-perdas/motivos');
            // A resposta tem estrutura: { data: [array de motivos] }
            const motivosData = response.data?.data || response.data || [];
            setMotivos(Array.isArray(motivosData) ? motivosData : []);
        } catch (error) {
            toast.error('Erro ao carregar motivos de lançamento de perdas');
            setMotivos([]); // Garantir que seja um array vazio em caso de erro
        } finally {
            setIsLoading(false);
        }
    };

    // Abrir modal de confirmação
    const abrirConfirmacao = (dadosPecas, dadosAmostra, onConfirmar) => {
        setPecas(Array.isArray(dadosPecas) ? dadosPecas : []);
        setAmostra(dadosAmostra || {});
        setCallbackFinalizar(() => onConfirmar);
        setShowConfirmacao(true);
        carregarMotivos();
    };

    // Abrir modal de perdas
    const abrirModal = (dadosPecas, dadosAmostra, onFinalizar) => {
        setPecas(Array.isArray(dadosPecas) ? dadosPecas : []);
        setAmostra(dadosAmostra || {});
        setCallbackFinalizar(() => onFinalizar);
        setShowModal(true);
        carregarMotivos();
    };

    // Confirmar lançamento de perdas
    const confirmarLancamentoPerdas = () => {
        setShowConfirmacao(false);
        setShowModal(true);
    };

    // Cancelar e executar finalização normal
    const cancelarLancamentoPerdas = () => {
        setShowConfirmacao(false);
        if (callbackFinalizar) {
            callbackFinalizar();
            setCallbackFinalizar(null);
        }
    };

    // Fechar modal
    const fecharModal = () => {
        setShowModal(false);
        setShowSuccess(false);
        setErrosLancamento([]);
        setCallbackFinalizar(null);
        resetarFormulario();
    };

    // Fechar confirmação
    const fecharConfirmacao = () => {
        setShowConfirmacao(false);
        setCallbackFinalizar(null);
    };

    const resetarFormulario = () => {
        setMotivoSelecionado(-1);
        setDestino('');
        setObservacao('');
        setResponsavel(userLogin);
        setRequisitante('CONTROLE DE QUALIDADE');
        setReqMaterial('');
        setQuantidadeBatch('');
        setMotivoBatch(-1);
    };

    const validarEstoquePecas = (quantidadeLancar) => {
        const pecasSemEstoque = pecas.filter(peca => {
            const estoqueDisponivel = parseFloat(peca.metroForn || peca.relogio || 0);
            return parseFloat(quantidadeLancar) > estoqueDisponivel;
        });

        if (pecasSemEstoque.length > 0) {
            const nomePecas = pecasSemEstoque.map(p => p.peca).join(', ');
            toast.error(`As seguintes peças não possuem estoque suficiente: ${nomePecas}`);
            return false;
        }

        return true;
    };

    const validarFormulario = () => {
        if (pecas.length === 0) {
            toast.warning('Não há peças para lançar perdas');
            return false;
        }

        if (isBatchMode) {
            const qtdBatch = parseFloat(quantidadeBatch);

            if (!quantidadeBatch || qtdBatch <= 0) {
                toast.warning('Informe uma quantidade válida para o lançamento em lote');
                return false;
            }

            if (parseInt(motivoBatch) === -1) {
                toast.warning('Selecione um motivo para o lançamento em lote');
                return false;
            }

            // Removida validação de estoque no modo batch

            return true;
        }

        const pecasComQuantidade = pecas.filter(peca => (peca.qtdeLancar || 0) > 0);

        if (pecasComQuantidade.length === 0) {
            toast.warning('Defina pelo menos uma quantidade de perda maior que zero');
            return false;
        }

        const pecasSemMotivo = pecasComQuantidade.filter(peca => {
            const motivoSelecionado = peca.motivoSelecionado;
            return !motivoSelecionado || parseInt(motivoSelecionado) === -1;
        });

        if (pecasSemMotivo.length > 0) {
            const pecasTexto = pecasSemMotivo.map(p => p.peca).join(', ');
            toast.warning(`As peças com quantidade de perda devem ter motivo selecionado: ${pecasTexto}`);
            return false;
        }

        return true;
    };

    const processarLancamentoPerdas = async (gerarEtiquetas = true) => {
        if (isProcessing) {
            toast.warning('Aguarde, o lançamento está sendo processado...');
            return;
        }

        if (!validarFormulario()) {
            return;
        }

        const baixarEtiqueta = async (peca, filial, numero) => {
            try {
                const response = await api.get('/mp/etiqueta-pos-perda', {
                    params: { peca, filial },
                    responseType: 'blob'
                });

                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;

                link.setAttribute('download', `${numero}${peca}-mp-pos-perda.txt`);
                document.body.appendChild(link);
                link.click();

                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);
            } catch (error) {
                console.error(`Erro ao baixar etiqueta da peça ${peca}:`, error);
                toast.error(`Erro ao gerar etiqueta para a peça ${peca}`);
            }
        };

        try {
            setIsProcessing(true);
            setIsLoading(true);

            let pecasParaLancar;

            if (isBatchMode) {
                const qtdBatch = parseFloat(quantidadeBatch);
                const motivoId = parseInt(motivoBatch);

                pecasParaLancar = pecas.map(peca => ({
                    ...peca,
                    qtdeLancar: qtdBatch,
                    motivoSelecionado: motivoId
                }));
            } else {
                pecasParaLancar = pecas.filter(peca => {
                    const qtdeLancar = peca.qtdeLancar || 0;
                    const motivoSelecionado = peca.motivoSelecionado;
                    return qtdeLancar > 0 && motivoSelecionado && parseInt(motivoSelecionado) !== -1;
                });
            }

            if (pecasParaLancar.length === 0) {
                toast.warning('Nenhuma peça válida para lançamento.');
                return;
            }

            const qtdeTotalMaterial = pecasParaLancar.reduce((total, peca) => {
                return total + (parseFloat(peca.qtdeLancar) || 0);
            }, 0);

            const lancamento = {
                usuario: userLogin,
                filial: "REVISAO MP SOMA RBX",
                emissao: new Date().toISOString(),
                responsavel: userLogin,
                destino: "QUALIDADE",
                obs: observacao || 'Lançamento de perdas via sistema de revisão',
                requisitante: "CONTROLE DE QUALIDADE",
                origem: isBatchMode ? "RevisaoCemPorcentoRetorno" : "Revisao",
                materiais: [{
                    material: amostra.material || '',
                    cor_material: amostra.cormaterial || '',
                    qtde: qtdeTotalMaterial,
                    custo: 0,
                    data_custo: new Date().toISOString(),
                    fator_conversao_unidade: 1,
                    rateio_centro_custo: "00000",
                    rateio_filial: "00000",
                    pecas: pecasParaLancar.map(peca => ({
                        peca: peca.peca,
                        partida: "UNICA",
                        qtde: (peca.qtdeLancar || 0).toString(),
                        idMotivo: parseInt(peca.motivoSelecionado)
                    }))
                }]
            };

            const lancamentos = [lancamento];

            // Processar todos os lançamentos
            const resultados = [];
            const erros = [];

            for (const lancamento of lancamentos) {
                try {
                    const response = await api.post('/mp/lancamento-perdas/processar', lancamento);
                    resultados.push(response.data);
                } catch (errorLancamento) {

                    // Tratar erro 409 especificamente
                    if (errorLancamento.response?.status === 409) {
                        const mensagemErro = errorLancamento.response?.data?.message || 'Erro de conflito no lançamento';
                        erros.push(mensagemErro);
                        toast.error(mensagemErro);
                    } else {
                        const mensagemErro = errorLancamento.response?.data?.message || 'Erro desconhecido no lançamento';
                        erros.push(mensagemErro);
                        toast.error(mensagemErro);
                    }
                }
            }

            // Armazenar erros no estado
            setErrosLancamento(erros);
            // Se houve pelo menos um sucesso
            if (resultados.length > 0) {
                if (gerarEtiquetas) {
                    for (const lanca of lancamentos) {
                        if (lanca.materiais) {
                            for (const mat of lanca.materiais) {
                                if (mat.qtde > 0 && mat.pecas) {
                                    for (const p of mat.pecas) {
                                        await baixarEtiqueta(p.peca, lanca.filial, 1); // 1ª cópia
                                        await baixarEtiqueta(p.peca, lanca.filial, 2); // 2ª cópia
                                    }
                                }
                            }
                        }
                    }
                }

                // Consolidar req_materials
                const reqMaterials = resultados.map(resultado => resultado.req_material).join(', ');
                setReqMaterial(reqMaterials);
                setShowSuccess(true);

                const totalLancamentos = resultados.length;
                const totalErros = erros.length;

                if (totalErros > 0) {
                    toast.warning(`${totalLancamentos} lançamento(s) realizado(s) com sucesso, mas ${totalErros} falharam. Verifique as mensagens de erro.`);
                } else {
                    toast.success(`${totalLancamentos} lançamento(s) de perdas realizado(s) com sucesso!`);
                    // Se não há erros, executar callback de finalização
                    if (callbackFinalizar && totalErros === 0) {
                        setTimeout(() => {
                            callbackFinalizar();
                            setCallbackFinalizar(null);
                        }, 2000); // Aguarda 2 segundos para mostrar sucesso
                    }
                }
            } else {
                // Todos os lançamentos falharam
                toast.error('Todos os lançamentos falharam. Verifique as mensagens de erro e tente novamente.');
            }

        } catch (error) {
            // Tratar erro 409 no nível geral também
            if (error.response?.status === 409) {
                const mensagemErro = error.response?.data?.message || 'Erro de conflito no lançamento de perdas';
                toast.error(mensagemErro);
            } else {
                const mensagemErro = error.response?.data?.message || 'Erro ao processar lançamento de perdas';
                toast.error(mensagemErro);
            }
        } finally {
            setIsLoading(false);
            setIsProcessing(false);
        }
    };

    // Calcular perdas de uma peça
    const calcularPerdasPeca = (peca) => {
        // Se a peça já tem defeitos como propriedade
        if (peca?.defeitos && Array.isArray(peca.defeitos)) {
            const perdas = peca.defeitos.reduce((total, defeito) => {
                // Verificar se tem perda marcada como true/boolean ou string/number
                const temPerda = defeito.perda === true || defeito.perda === 'true' || defeito.perda === 1 || defeito.perda === "1";
                const quantidadePerda = defeito.qntPerda;

                // Verificar se quantidade de perda é válida (maior que 0)
                const qtdNumerica = parseInt(quantidadePerda) || 0;

                if (temPerda && qtdNumerica > 0) {
                    return total + qtdNumerica;
                } else if (qtdNumerica > 0) {
                    // Caso a quantidade seja > 0 mas perda não esteja marcada explicitamente
                    return total + qtdNumerica;
                }
                return total;
            }, 0);
            return perdas;
        }

        return 0;
    };

    // Handlers para inputs
    const handleMotivoChange = (e) => {
        setMotivoSelecionado(e.target.value);
    };

    const handleDestinoChange = (e) => {
        setDestino(e.target.value);
    };

    const handleObservacaoChange = (e) => {
        setObservacao(e.target.value);
    };

    const handleResponsavelChange = (e) => {
        setResponsavel(e.target.value);
    };

    const handleRequisitanteChange = (e) => {
        setRequisitante(e.target.value);
    };

    const handleQuantidadeBatchChange = (e) => {
        const value = e.target.value.replace(',', '.');
        if (/^\d*\.?\d*$/.test(value)) {
            setQuantidadeBatch(value);
        }
    };

    const handleMotivoBatchChange = (e) => {
        setMotivoBatch(e.target.value);
    };

    const toggleBatchMode = () => {
        setIsBatchMode(!isBatchMode);
        setQuantidadeBatch('');
        setMotivoBatch(-1);
    };

    return (
        <LancamentoPerdasContext.Provider
            value={{
                showModal,
                setShowModal,
                showConfirmacao,
                setShowConfirmacao,
                isLoading,
                isProcessing,
                showSuccess,

                pecas,
                amostra,
                motivos,
                reqMaterial,
                errosLancamento,
                motivoSelecionado,
                destino,
                observacao,
                responsavel,
                requisitante,
                habilitarModoLote,
                isBatchMode,
                quantidadeBatch,
                motivoBatch,
                abrirModal,
                abrirConfirmacao,
                fecharModal,
                fecharConfirmacao,
                confirmarLancamentoPerdas,
                cancelarLancamentoPerdas,
                processarLancamentoPerdas,
                calcularPerdasPeca,
                validarEstoquePecas,
                toggleBatchMode,
                handleMotivoChange,
                handleDestinoChange,
                handleObservacaoChange,
                handleResponsavelChange,
                handleRequisitanteChange,
                handleQuantidadeBatchChange,
                handleMotivoBatchChange,
            }}
        >
            {children}
        </LancamentoPerdasContext.Provider>
    );
};
